import type { BeatEvent, Occurrence, RevisionContent } from '../schema/v1'
import { freshIds } from './reconcile'

/**
 * La secuencia de interpretación con sus repeticiones (§3.2): el orden en que suenan las
 * secciones. Repetir, mover o quitar una aparición arrastra su parte del mapa de tiempos, que
 * sigue el orden de la secuencia. Ninguna sección se queda sin aparecer.
 */

/** Reordena los eventos del mapa para que sigan la secuencia y descarta los que ya no apuntan a nada. */
function followSequence(content: RevisionContent, sequence: Occurrence[]): RevisionContent {
  const byOccurrence = new Map<string, BeatEvent[]>()
  for (const event of content.beatMap.events) {
    const list = byOccurrence.get(event.occurrence) ?? []
    list.push(event)
    byOccurrence.set(event.occurrence, list)
  }
  const events = sequence.flatMap((occurrence) => byOccurrence.get(occurrence.id) ?? [])
  const beatMap =
    content.beatMap.status !== 'absent' && events.length === 0
      ? { status: 'absent' as const, events: [] }
      : { ...content.beatMap, events }
  return { ...content, sequence, beatMap }
}

/** Repite una aparición justo detrás de ella, con su parte del mapa de tiempos. */
export function repeatOccurrence(content: RevisionContent, occurrenceId: string): RevisionContent {
  const index = content.sequence.findIndex((occurrence) => occurrence.id === occurrenceId)
  if (index < 0) return content
  const source = content.sequence[index]!
  const copy: Occurrence = { id: freshIds(content)('o'), section: source.section }
  const sequence = [...content.sequence]
  sequence.splice(index + 1, 0, copy)
  const copied = content.beatMap.events
    .filter((event) => event.occurrence === source.id)
    .map((event) => ({ ...event, occurrence: copy.id }))
  const withCopy: RevisionContent = {
    ...content,
    beatMap: { ...content.beatMap, events: [...content.beatMap.events, ...copied] },
  }
  return followSequence(withCopy, sequence)
}

/** Mueve una aparición un puesto antes (−1) o después (+1). */
export function moveOccurrence(
  content: RevisionContent,
  occurrenceId: string,
  delta: -1 | 1,
): RevisionContent {
  const index = content.sequence.findIndex((occurrence) => occurrence.id === occurrenceId)
  const target = index + delta
  if (index < 0 || target < 0 || target >= content.sequence.length) return content
  const sequence = [...content.sequence]
  const [moved] = sequence.splice(index, 1)
  sequence.splice(target, 0, moved!)
  return followSequence(content, sequence)
}

/** ¿Se puede quitar? Solo si su sección sigue apareciendo en otro sitio. */
export function canRemoveOccurrence(content: RevisionContent, occurrenceId: string): boolean {
  const occurrence = content.sequence.find((item) => item.id === occurrenceId)
  if (!occurrence) return false
  return content.sequence.some(
    (item) => item.id !== occurrenceId && item.section === occurrence.section,
  )
}

/** Quita una repetición. La última aparición de una sección no se quita: se borraría su letra de la vista. */
export function removeOccurrence(content: RevisionContent, occurrenceId: string): RevisionContent {
  if (!canRemoveOccurrence(content, occurrenceId)) return content
  return followSequence(
    content,
    content.sequence.filter((occurrence) => occurrence.id !== occurrenceId),
  )
}
